/* eslint-disable rxjs-x/finnish */
import { ESCAPE, hasModifierKey } from '@angular/cdk/keycodes';
import { OverlayRef } from '@angular/cdk/overlay';
import { Observable, Subject, merge } from 'rxjs';
import { filter, take } from 'rxjs/operators';

import { DialogContainerComponent } from './dialog-container';

/**
 * Reference to a dialog dispatched from the dialog service.
 */
export class DialogRef<T = unknown, R = unknown> {
  /** Instance of the component making up the content of the dialog. */
  public instance!: T;

  /**
   * Instance of the component into which the dialog content is projected.
   *
   * @docs-private
   */
  public containerInstance: DialogContainerComponent;

  /** Whether the user is allowed to close the dialog. */
  public disableClose: boolean | undefined;

  /** Subject for notifying the user that the dialog has been dismissed. */
  private readonly _afterDismissed$ = new Subject<R | undefined>();

  /** Subject for notifying the user that the dialog has opened and appeared. */
  private readonly _afterOpened$ = new Subject<void>();

  /** Result to be passed down to the `afterDismissed` stream. */
  private _result: R | undefined;

  /** Handle to the timeout that's running as a fallback in case the exit animation doesn't fire. */
  private _closeFallbackTimeout!: number;

  constructor(
    containerInstance: DialogContainerComponent,
    public readonly overlayRef: OverlayRef
  ) {
    this.containerInstance = containerInstance;
    this.disableClose = containerInstance.dialogConfig.disableClose;

    // Emit when opening animation completes
    containerInstance._animationStateChanged
      .pipe(
        filter((event) => event.phaseName === 'done' && event.toState === 'visible'),
        take(1)
      )
      .subscribe(() => {
        this._afterOpened$.next();
        this._afterOpened$.complete();
      });

    // Dispose overlay when closing animation is complete
    containerInstance._animationStateChanged
      .pipe(
        filter((event) => event.phaseName === 'done' && event.toState === 'hidden'),
        take(1)
      )
      .subscribe(() => {
        clearTimeout(this._closeFallbackTimeout);
        overlayRef.dispose();
      });

    overlayRef
      .detachments()
      .pipe(take(1))
      .subscribe(() => {
        this._afterDismissed$.next(this._result);
        this._afterDismissed$.complete();
      });

    merge(overlayRef.backdropClick(), overlayRef.keydownEvents().pipe(filter((event) => event.keyCode === ESCAPE))).subscribe((event) => {
      if (!this.disableClose && (event.type !== 'keydown' || !hasModifierKey(event as KeyboardEvent))) {
        event.preventDefault();
        this.dismiss();
      }
    });
  }

  /**
   * Dismisses the dialog.
   *
   * @param result Data to be passed back to the dialog opener.
   */
  public dismiss(result?: R): void {
    if (!this._afterDismissed$.closed) {
      // Transition the backdrop in parallel to the dialog.
      this.containerInstance._animationStateChanged
        .pipe(
          filter((event) => event.phaseName === 'start'),
          take(1)
        )
        .subscribe((event) => {
          // The logic that disposes of the overlay depends on the exit animation completing, however
          // it isn't guaranteed if the parent view is destroyed while it's running. Add a fallback
          // timeout which will clean everything up if the animation hasn't fired within the specified
          // amount of time plus 100ms. We don't need to run this outside the NgZone, because for the
          // vast majority of cases the timeout will have been cleared before it has fired.
          this._closeFallbackTimeout = window.setTimeout(() => {
            this.overlayRef.dispose();
          }, event.totalTime + 100);

          this.overlayRef.detachBackdrop();
        });

      this._result = result;
      this.containerInstance.exit();
    }
  }

  /** Gets an observable that is notified when the dialog is finished closing. */
  public afterDismissed(): Observable<R | undefined> {
    return this._afterDismissed$;
  }

  /** Gets an observable that is notified when the dialog has opened and appeared. */
  public afterOpened(): Observable<void> {
    return this._afterOpened$;
  }

  /**
   * Gets an observable that emits when the overlay's backdrop has been clicked.
   */
  public backdropClick(): Observable<MouseEvent> {
    return this.overlayRef.backdropClick();
  }

  /**
   * Gets an observable that emits when keydown events are targeted on the overlay.
   */
  public keydownEvents(): Observable<KeyboardEvent> {
    return this.overlayRef.keydownEvents();
  }
}
